import {
  computed,
  type ComputedRef,
} from 'vue';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {
  PageState,
  type Session,
} from '@/ts/types/dto/interventions-dto';

import {useInterventionStore} from '@/ts/store/intervention-store';
import {
  useSession,
  type UseSessionRes,
} from '@/ts/composables/pure/use-provided-session';


/** Computed ref to the 1-based index of the current (first non-completed) page in a session */
export interface UseSessionCurrentRes {
  currentPageId: ComputedRef<number | null>;
}

/** Looks up a session by ID within the current intervention and provides session-level computed data */
export function useSessionId(id: ComputedRef<UuidDTO | null>): UseSessionRes {
  const interventionRef = useInterventionStore();
  const interventionComp = computed(() => interventionRef.value!);
  const session = computed<Session | null>(() => interventionComp.value?.sessions.find(ses => ses.id === id.value) ?? null);
  return useSession(interventionComp, session);
}

/** Computes the 1-based index of the first non-completed page in a session (null if all completed) */
export function useSessionProgress(sid: ComputedRef<UuidDTO | null>): UseSessionCurrentRes {
  const {session} = useSessionId(sid);
  const currentPageId = computed(() => {
    if (!session.value) {
      return null;
    }
    const index = session.value!.pages.findIndex(page => page.state !== PageState.COMPLETED );
    if (index < 0) {
      return null;
    }
    return index + 1;
  });
  return {currentPageId};
}
